/**
 * Agent API Service
 *
 * Typed client for all agent-related endpoints:
 * - Agent CRUD
 * - Agent activation/deactivation
 * - Skills list for picker
 * - Models list for selector
 */

import { apiClient } from './api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AgentScope = 'org' | 'team' | 'user';
export type AgentStatus = 'active' | 'disabled' | 'draft';

export interface ToolConfig {
  name: string;
  enabled: boolean;
  config?: Record<string, unknown>;
}

export interface Agent {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  system_prompt: string;
  default_model_id: string | null;
  default_model_name: string | null;
  tools_config: ToolConfig[];
  skill_refs: string[];
  max_thinking_budget: number | null;
  scope: AgentScope;
  owner_team_id: string | null;
  owner_user_id: string | null;
  status: AgentStatus;
  version: number;
  is_activated_by_current_user: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateAgentInput {
  name: string;
  slug: string;
  description?: string;
  system_prompt: string;
  default_model_id?: string;
  tools_config?: ToolConfig[];
  skill_refs?: string[];
  max_thinking_budget?: number;
  scope: AgentScope;
  owner_team_id?: string;
}

export interface UpdateAgentInput {
  name?: string;
  description?: string;
  system_prompt?: string;
  default_model_id?: string;
  tools_config?: ToolConfig[];
  skill_refs?: string[];
  max_thinking_budget?: number;
  status?: AgentStatus;
}

export interface ListAgentsParams {
  scope?: AgentScope | 'all';
  status?: AgentStatus;
  page?: number;
  per_page?: number;
}

export interface ListAgentsResponse {
  data: Agent[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export type SkillType = 'knowledge' | 'prompt' | 'toolset' | 'mcp_server';

export interface SkillSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  type: SkillType;
  scope: AgentScope;
  status: string;
}

export interface ListSkillsResponse {
  data: SkillSummary[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ModelSummary {
  id: string;
  display_name: string;
  provider_name: string;
  available: boolean;
}

// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------

const AGENTS_PATH = '/v1/agents';
const SKILLS_PATH = '/v1/skills';

export const agentApi = {
  /** List agents */
  async list(params?: ListAgentsParams): Promise<ListAgentsResponse> {
    const query = new URLSearchParams();
    if (params?.scope) query.set('scope', params.scope);
    if (params?.status) query.set('status', params.status);
    if (params?.page) query.set('page', String(params.page));
    if (params?.per_page) query.set('per_page', String(params.per_page));
    const qs = query.toString();
    return apiClient.get<ListAgentsResponse>(`${AGENTS_PATH}${qs ? `?${qs}` : ''}`);
  },

  /** Get agent by ID */
  async get(id: string): Promise<Agent> {
    return apiClient.get<Agent>(`${AGENTS_PATH}/${id}`);
  },

  /** Create agent */
  async create(input: CreateAgentInput): Promise<Agent> {
    return apiClient.post<Agent>(AGENTS_PATH, input);
  },

  /** Update agent (bumps version) */
  async update(id: string, input: UpdateAgentInput): Promise<Agent> {
    return apiClient.patch<Agent>(`${AGENTS_PATH}/${id}`, input);
  },

  /** Soft-delete agent */
  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`${AGENTS_PATH}/${id}`);
  },

  /** Activate agent for current user */
  async activate(id: string): Promise<void> {
    return apiClient.post<void>(`${AGENTS_PATH}/${id}/activate`);
  },

  /** Deactivate agent for current user */
  async deactivate(id: string): Promise<void> {
    return apiClient.post<void>(`${AGENTS_PATH}/${id}/deactivate`);
  },

  /** Duplicate agent */
  async duplicate(id: string, input?: { new_name?: string; new_slug?: string; scope?: AgentScope }): Promise<Agent> {
    return apiClient.post<Agent>(`${AGENTS_PATH}/${id}/duplicate`, input ?? {});
  },

  /** List skills for picker (active, all scopes user can access) */
  async listSkills(params?: {
    scope?: AgentScope | 'all';
    type?: SkillType;
    page?: number;
    per_page?: number;
  }): Promise<ListSkillsResponse> {
    const query = new URLSearchParams();
    if (params?.scope) query.set('scope', params.scope);
    if (params?.type) query.set('type', params.type);
    if (params?.page) query.set('page', String(params.page));
    if (params?.per_page) query.set('per_page', String(params.per_page));
    query.set('status', 'active');
    const qs = query.toString();
    return apiClient.get<ListSkillsResponse>(`${SKILLS_PATH}?${qs}`);
  },
};
